import type { IRecord } from '@platform/core/types';
import type { ILawCard } from './ILawCard';
import type { ILawCardSection } from './ILawCardSection';
import type { ILawLabels } from './ILawLabels';
import { lawValue } from './lawGroups';
import { cardTone, levelTone } from './lawTones';

/** Записи дочірнього типу законопроєкту (за номером). */
type LawRecordsOf = (rtKey: string) => IRecord[];

const join = (parts: string[]) => parts.filter(Boolean).join(' · ');

/** «Ст. N» статті за значенням поля-посилання (ідентифікатор) серед статей законопроєкту (прототип artN); невідома — ''. */
const articleOf = (arts: readonly IRecord[], labels: ILawLabels) => (r: IRecord) => {
  const id = lawValue(r, 'article');
  const a = id ? arts.find((x) => x.id === id) : undefined;
  return a ? labels.article(a.values['num']) : '';
};

/**
 * Секції «Аналітичних записів» (прототип v4 lawAnl): правові проблеми (рівень — вплив), ризики (вплив), можливості (цінність), гіпотези
 * (впевненість), питання (серйозність) — номер, рівень, назва, «стаття · вид …» і текст. Чиста функція.
 */
export function analysisSections(of: LawRecordsOf, arts: readonly IRecord[], labels: ILawLabels): ILawCardSection[] {
  const art = articleOf(arts, labels);
  const o = (rt: string, r: IRecord, f: string) => labels.option(rt, f, r.values[f]);
  const specs: [string, string, (r: IRecord) => string, (r: IRecord) => string][] = [
    ['lprob', 'impact', (r) => join([art(r), o('lprob', r, 'group')]), (r) => lawValue(r, 'evidence')],
    ['lrisk', 'impact', (r) => join([art(r), o('lrisk', r, 'rtype'), o('lrisk', r, 'likelihood')]), (r) => lawValue(r, 'mitigation')],
    ['lopp', 'value', (r) => o('lopp', r, 'benefit'), (r) => lawValue(r, 'how')],
    ['lhyp', 'confidence', (r) => lawValue(r, 'indicator'), (r) => [lawValue(r, 'assumption'), lawValue(r, 'expected')].filter(Boolean).join(' ')],
    ['lissue', 'severity', (r) => join([art(r), o('lissue', r, 'itype')]), () => ''],
  ];
  return specs.map(([rt, level, meta, text]) => ({
    rt,
    title: labels.name(rt),
    cards: of(rt).map((r) => ({
      rt, record: r, code: r.display, label: r.title, level: o(rt, r, level), tone: levelTone(r.values[level]), meta: meta(r),
      status: labels.status(rt, r.status), text: text(r),
    })),
  }));
}

/**
 * Секції «Правок» (прототип v4 lawEdits · lawComs · lawVers): правки («стаття · вид · автор», запропонований текст), коментарі до тексту
 * («стаття · автор», коментар) і версії за датою від новішої («номер · дата», зміни); пігулка — статус. Чиста функція.
 */
export function workSections(of: LawRecordsOf, arts: readonly IRecord[], labels: ILawLabels): ILawCardSection[] {
  const art = articleOf(arts, labels);
  const card = (rt: string, r: IRecord, meta: string, text: string): ILawCard => ({
    rt, record: r, code: r.display, label: r.title, level: labels.status(rt, r.status), tone: cardTone(r.status), meta, status: labels.status(rt, r.status), text,
  });
  const byDate = of('ldver').map((r, i) => ({ r, i }))
    .sort((a, b) => lawValue(b.r, 'vdate').localeCompare(lawValue(a.r, 'vdate')) || a.i - b.i).map((x) => x.r);
  const edits = of('ldedit').map((r) => card('ldedit', r, join([art(r), labels.option('ldedit', 'etype', r.values['etype']), lawValue(r, 'author')]), lawValue(r, 'proposed')));
  const comments = of('ldcom').map((r) => card('ldcom', r, join([art(r), lawValue(r, 'author')]), lawValue(r, 'comment')));
  const versions = byDate.map((r) => card('ldver', r, join([lawValue(r, 'vno'), lawValue(r, 'vdate')]), lawValue(r, 'changes')));
  return [
    { rt: 'ldedit', title: labels.name('ldedit'), cards: edits },
    { rt: 'ldcom', title: labels.name('ldcom'), cards: comments },
    { rt: 'ldver', title: labels.name('ldver'), cards: versions },
  ];
}
